// Image Studio API Service — supports multiple operations via single Lambda

import { uploadFilesViaPresignedUrls } from './uploadHelper.js';
import { apiFetch } from '../../lib/apiFetch.js';

const API_BASE_URL = 'https://css2g9ujye.execute-api.ap-south-1.amazonaws.com';

// Files larger than this can't go through API Gateway's 10 MB JSON limit
// (base64 inflation pushes the wire payload over the cap quickly). Anything
// at or above this threshold uses the presigned-PUT direct-to-S3 flow. The
// inline-base64 path is kept for tiny files because it skips an HTTP round
// trip — set to 0 to force every upload through presigned PUTs.
const PRESIGNED_UPLOAD_THRESHOLD_BYTES = 0; // always use presigned uploads

// Hard sanity cap. Files above this size get rejected client-side with a
// clear error rather than failing partway through a 5-min upload.
const MAX_FILE_BYTES = 100 * 1024 * 1024; // 100 MB

/** Try to parse a non-OK fetch response into a usable error message. */
async function readErrorMessage(response, fallback) {
  // We deliberately don't call response.json() unconditionally — some
  // upstream failures (API Gateway 413, CORS preflight rejections,
  // CloudFront error pages) return HTML or empty bodies. Calling .json()
  // on those throws and the minified prod bundle surfaces it as
  // `g.json is not a function`. The clone + safe-parse pattern below
  // gives us a useful message in all cases.
  try {
    const cloned = response.clone();
    const data = await cloned.json();
    if (data && data.error) return data.error;
  } catch { /* fall through to text */ }
  try {
    const text = await response.text();
    if (text) return text.slice(0, 300);
  } catch { /* ignore */ }
  return fallback || `HTTP ${response.status}`;
}

export const imageStudioApi = {
  createRunId(username) {
    const user = (username || 'unknown').replace(/[^a-zA-Z0-9]/g, '_');
    const ts = new Date().toISOString().replace(/[-:T]/g, '').slice(0, 15);
    return `${user}_${ts}`;
  },

  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  },

  // Generic process — sends operation + params to Lambda.
  // `input` can be either an array of File objects (mode 'files') or an
  // array of { url, name, product?, slot? } objects (mode 'urls'). In files
  // mode the helper uploads each file to S3 via a presigned PUT and we
  // send `image_keys` to the Lambda; in URL mode the Lambda fetches each
  // URL server-side so the browser doesn't have to deal with CORS on every
  // CDN out there.
  //
  // URL-mode runs always go async on the server (the worker fetches URLs
  // with the full 900s budget instead of API Gateway's 29s). The poll
  // path is shared with image_keys runs.
  async processImages(input, runId, params, onProgress, opts = {}) {
    const mode = opts.mode || 'files';
    const total = input.length;
    if (onProgress) onProgress({ completed: 0, total, phase: 'uploading' });

    if (mode === 'urls') {
      // URL mode — the handler validates and stashes input.json, the
      // worker fetches the URLs and processes. Always returns
      // { run_id, status: "processing" } now.
      if (onProgress) onProgress({ completed: 0, total, phase: 'submitting' });
      const response = await apiFetch(`${API_BASE_URL}/api/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ run_id: runId, image_urls: input, ...params })
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, `Processing failed: ${response.status}`));
      }
      const data = await response.json();

      if (data.status === 'processing') {
        if (onProgress) onProgress({ completed: 0, total, phase: 'processing' });
        return await this._pollAsyncJob(runId, total, onProgress, params.operation);
      }

      // Defensive fallback: if the backend ever responds sync (it
      // shouldn't post-port — every URL run goes async), still surface
      // the rows.
      const results = data.results || [];
      for (const r of results) {
        if (r.view_url && r.view_url.startsWith('/'))
          r.view_url = `${API_BASE_URL}${r.view_url}`;
      }
      if (onProgress) onProgress({ completed: total, total });
      return results;
    }

    // Files mode — presigned PUT upload, then process by S3 keys.

    // Client-side hard cap. Surface the error before we hit the wire.
    for (const f of input) {
      if (f.size > MAX_FILE_BYTES) {
        throw new Error(
          `${f.name} is ${Math.round(f.size / 1024 / 1024)} MB. Max is ${MAX_FILE_BYTES / 1024 / 1024} MB.`
        );
      }
    }

    // Decide which files go via presigned upload vs inline base64. With
    // PRESIGNED_UPLOAD_THRESHOLD_BYTES at 0, every file goes through
    // presigned PUTs. Kept as two branches in case we want to flip the
    // threshold for some operations later.
    const presignFiles = input.filter(f => f.size >= PRESIGNED_UPLOAD_THRESHOLD_BYTES);
    const inlineFiles = input.filter(f => f.size < PRESIGNED_UPLOAD_THRESHOLD_BYTES);

    let imageKeys = [];
    let inlineImages = [];

    if (presignFiles.length > 0) {
      const { keys, failures } = await uploadFilesViaPresignedUrls({
        files: presignFiles,
        apiBaseUrl: API_BASE_URL,
        runId,
        operation: params.operation || 'resize',
        concurrency: 4,
        onProgress: (p) => {
          if (onProgress) onProgress({
            completed: p.uploaded, total: p.total, phase: p.phase || 'uploading',
          });
        },
      });
      imageKeys = keys.filter(Boolean); // drop empty (failed) entries

      if (failures.length > 0 && imageKeys.length === 0) {
        throw new Error(
          `All uploads failed: ${failures.map(f => `${f.name}: ${f.error}`).join('; ')}`
        );
      }
      // Partial failures get surfaced when the run results come back —
      // the Lambda returns failed rows in the CSV alongside successes.
    }

    if (inlineFiles.length > 0) {
      for (const file of inlineFiles) {
        inlineImages.push({ name: file.name, data: await this.fileToBase64(file) });
      }
    }

    // Build the process request. The Lambda accepts EITHER image_keys OR
    // images, but not both in the same request. If we have both, do two
    // calls with the same run_id (the Lambda merges results downstream).
    if (imageKeys.length > 0 && inlineImages.length > 0) {
      // Edge case — happens only if someone changes the threshold to a
      // non-zero value and uploads a mix of small and large files. For
      // simplicity, we send the keys path only and inline-encode the small
      // files into images bytes via a second helper call. Today this branch
      // is unreachable because the threshold is 0.
      throw new Error('Mixed inline + presigned upload not implemented — set PRESIGNED_UPLOAD_THRESHOLD_BYTES to 0');
    }

    const requestBody = imageKeys.length > 0
      ? { run_id: runId, image_keys: imageKeys, ...params }
      : { run_id: runId, images: inlineImages, ...params };

    if (onProgress) onProgress({ completed: total, total, phase: 'submitting' });

    const response = await apiFetch(`${API_BASE_URL}/api/process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Processing failed: ${response.status}`));
    }

    const data = await response.json();

    if (data.status === 'processing') {
      // Async path — Lambda invoked itself. Poll until the run completes,
      // then fetch the results so sync-style callers (Resize/Convert UI)
      // get the result rows back inline.
      if (onProgress) onProgress({ completed: 0, total, phase: 'processing' });
      const finalResults = await this._pollAsyncJob(runId, total, onProgress, params.operation);
      return finalResults;
    }

    const results = data.results || [];
    for (const r of results) {
      if (r.view_url && r.view_url.startsWith('/'))
        r.view_url = `${API_BASE_URL}${r.view_url}`;
    }
    if (onProgress) onProgress({ completed: total, total, phase: 'done' });
    return results;
  },

  // Poll the runs endpoint until the async job completes. For sync ops
  // routed through async (Resize/Convert with image_keys), fetch the
  // results from /api/run-results once completed so the UI gets a result
  // array — same shape as the inline-sync return value. For ops the UI
  // already treats as async (Background, Lifestyle), the existing
  // contract is `return []` and the UI re-fetches via fetchRunResults
  // separately.
  async _pollAsyncJob(runId, total, onProgress, operation) {
    let attempts = 0;
    while (attempts < 60) {
      await new Promise(r => setTimeout(r, 5000));
      attempts++;
      try {
        const runsRes = await apiFetch(`${API_BASE_URL}/api/runs`);
        if (!runsRes.ok) continue;
        const runsData = await runsRes.json();
        const job = (runsData.runs || []).find(r => r.run_id === runId);
        if (job?.status === 'completed') {
          if (onProgress) onProgress({ completed: total, total, phase: 'done' });
          // For sync ops the UI expects results inline. Pull them.
          if (operation === 'resize' || operation === 'convert') {
            try {
              const detail = await this.fetchRunResults(runId, operation);
              return (detail.images || []).map(img => ({
                file: img.filename || '',
                resized_file: img.filename || '',
                view_url: img.view_url || '',
                s3_key: img.s3_key || '',
                width: img.width || 0,
                height: img.height || 0,
                ratio: '', method: operation, status: 'ok',
              }));
            } catch {
              return [];
            }
          }
          return [];
        }
        if (job?.status === 'failed') throw new Error(job.error || 'Processing failed');
      } catch (e) {
        if (e.message && e.message.includes('failed')) throw e;
      }
    }
    throw new Error('Processing timed out — check History tab');
  },

  async saveResults(runId, user, results, params) {
    const response = await apiFetch(`${API_BASE_URL}/api/save-results`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_id: runId, user, results, ...params })
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Save failed: ${response.status}`));
    }
    return response.json();
  },

  async downloadCsv(runId, operation) {
    const response = await apiFetch(`${API_BASE_URL}/api/download-csv`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ run_id: runId, operation: operation || '' })
    });
    if (!response.ok) throw new Error(`CSV download failed: ${response.status}`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${runId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },

  async listRuns() {
    const response = await apiFetch(`${API_BASE_URL}/api/runs`);
    if (!response.ok) throw new Error(`Failed to list runs: ${response.status}`);
    return response.json();
  },

  async fetchTemplates() {
    const response = await apiFetch(`${API_BASE_URL}/api/templates`);
    if (!response.ok) throw new Error(`Failed to fetch templates: ${response.status}`);
    return response.json();
  },

  async fetchRunResults(runId, operation) {
    const url = new URL(`${API_BASE_URL}/api/run-results`);
    url.searchParams.set('run_id', runId);
    if (operation) url.searchParams.set('operation', operation);
    const response = await apiFetch(url.toString());
    if (!response.ok) throw new Error(`Failed to fetch run results: ${response.status}`);
    const data = await response.json();
    // Convert relative view_urls to absolute
    (data.images || []).forEach(img => {
      if (img.view_url?.startsWith('/')) img.view_url = `${API_BASE_URL}${img.view_url}`;
    });
    (data.original_images || []).forEach(img => {
      if (img.view_url?.startsWith('/')) img.view_url = `${API_BASE_URL}${img.view_url}`;
    });
    if (data.reference_image_url?.startsWith('/')) {
      data.reference_image_url = `${API_BASE_URL}${data.reference_image_url}`;
    }
    if (data.original_image_url?.startsWith('/')) {
      data.original_image_url = `${API_BASE_URL}${data.original_image_url}`;
    }
    return data;
  },

  async previewLifestyle({ image, reference_image, use_case, aspect_ratio, custom_prompt, product_category, brand_tone, ad_copy, include_person, model_appearance }) {
    const response = await apiFetch(`${API_BASE_URL}/api/lifestyle/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        image, reference_image: reference_image || undefined,
        use_case: use_case || '',
        aspect_ratio: aspect_ratio || '',
        custom_prompt: custom_prompt || '',
        product_category: product_category || undefined,
        brand_tone: brand_tone || undefined,
        // Text to render in the image (Ad/Social). Sent as ad_copy so the
        // backend includes it in the Flux prompt directly.
        ad_copy: ad_copy || undefined,
        // Person in scene + model appearance
        include_person: include_person || undefined,
        model_appearance: model_appearance || undefined,
      }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Preview failed: ${response.status}`));
    }
    return response.json();
  },

  async submitFeedback({ rating, tool, category, comment, user, page }) {
    const response = await apiFetch(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        rating, tool: tool || 'overall',
        category: category || '', comment: comment || '',
        user: user || 'unknown', page: page || '',
      }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, `Feedback failed: ${response.status}`));
    }
    return response.json();
  },

  async regenerateWithTweak(sourceRunId, tweak, user) {
    const newRunId = this.createRunId(user || 'unknown');
    const response = await apiFetch(`${API_BASE_URL}/api/process`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        operation: 'lifestyle',
        run_id: newRunId,
        user: user || 'unknown',
        source_run_id: sourceRunId,
        tweak,
      }),
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, 'Regenerate failed'));
    }
    return { run_id: newRunId };
  },
};
